import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Color constants for consistency
const COLORS = {
  primary: '#065900',
  primaryHover: '#065900'
} as const;

interface Story {
  id: string;
  user: {
    id: string;
    username: string;
    avatar_url?: string;
    is_page?: boolean;
    is_music_page?: boolean;
    verified?: boolean;
    is_followed?: boolean;
  };
  viewed?: boolean;
}

interface StoryPreviewProps {
  currentUser: {
    id: string;
    username: string;
    avatar_url?: string;
    is_page?: boolean;
    is_music_page?: boolean;
    verified?: boolean;
  };
  stories: Story[];
  onStoryClick?: (storyId: string) => void;
  onAddStoryClick?: () => void;
  activeTab?: string;
}

export default function StoryPreview({ 
  currentUser, 
  stories, 
  onStoryClick, 
  onAddStoryClick, 
  activeTab = 'all' 
}: StoryPreviewProps) {
  const [hoveredStory, setHoveredStory] = useState<string | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize filtered stories to prevent unnecessary re-filtering
  const filteredStories = useMemo(() => {
    return activeTab === 'following'
      ? stories.filter(story => story.user?.is_followed === true)
      : stories;
  }, [stories, activeTab]);

  // Determine avatar shape based on user type (page vs profile)
  const getAvatarShape = useCallback((isPage?: boolean) => isPage ? "rounded-full" : "rounded-lg", []);

  // Optimized scroll handler with useCallback
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Scroll functions
  const scrollLeft = useCallback(() => {
    containerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    containerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  }, []);

  // Story hover handlers
  const handleStoryMouseEnter = useCallback((storyId: string) => {
    setHoveredStory(storyId);
  }, []);

  const handleStoryMouseLeave = useCallback(() => {
    setHoveredStory(null);
  }, []);

  // Set up scroll listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="relative mb-4">
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 dark:bg-gray-800/80 rounded-full p-1 shadow-md hover:bg-white/90 dark:hover:bg-gray-800/90 transition-colors"
          aria-label="Scroll stories left"
        >
          <ChevronLeft className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </button>
      )}

      {/* Right Arrow */}
      {showRightArrow && (
        <button
          onClick={scrollRight}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#065900] rounded-full p-2 shadow-lg hover:bg-[#054700] transition-colors"
          aria-label="Scroll stories right"
        >
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
      )}

      <div
        ref={containerRef}
        className="overflow-x-auto scrollbar-hide h-fit pb-0"
        role="region"
        aria-label="Stories"
      >
        <div className="flex gap-4 px-2 pb-0">
          {/* Add Story Button - Current User */}
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={onAddStoryClick}
            role="button"
            tabIndex={0}
            aria-label="Add your story"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onAddStoryClick?.();
              }
            }}
          >
            <div className="w-40 h-40 p-0.5 border-3 border-dashed border-gray-300 dark:border-gray-600 rounded-full transition-colors hover:border-[#065900] relative">
              <Avatar className={`w-full h-full ${getAvatarShape(currentUser.is_page)}`}>
                <AvatarImage 
                  src={currentUser.avatar_url || `https://ui-avatars.com/api/?name=${currentUser.username}&background=065900&color=fff`} 
                  alt={currentUser.username} 
                />
                <AvatarFallback className="bg-[#065900] text-white">
                  {currentUser.username.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              {/* Plus icon overlay */}
              <div className="absolute bottom-2 right-2 bg-[#065900] rounded-full p-1 border-2 border-white dark:border-gray-800">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
            <div className="flex items-center mt-2 mb-0">
              <span className="text-base truncate w-40 text-center">Your Story</span>
            </div>
          </div>

          {/* Other stories */}
          {filteredStories.map(story => {
            const isViewed = story.viewed;

            return (
              <div
                key={story.id}
                className="flex flex-col items-center cursor-pointer"
                onClick={() => onStoryClick?.(story.id)}
                onMouseEnter={() => handleStoryMouseEnter(story.id)}
                onMouseLeave={handleStoryMouseLeave}
                role="button"
                tabIndex={0}
                aria-label={`View ${story.user.username}'s story${story.user.verified ? ' (verified)' : ''}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onStoryClick?.(story.id);
                  }
                }}
              >
                <div className={`w-40 h-40 p-0.5 border-3 rounded-full transition-colors relative ${isViewed
                  ? 'border-gray-300'
                  : hoveredStory === story.id
                    ? `border-[${COLORS.primary}]`
                    : 'border-gradient-to-r from-purple-500 via-pink-500 to-orange-500'
                  }`}>
                  <Avatar className={`w-full h-full ${getAvatarShape(story.user.is_page)}`}>
                    <AvatarImage src={story.user.avatar_url} alt={story.user.username} />
                    <AvatarFallback>
                      <img
                        src={`https://ui-avatars.com/api/?name=${story.user.username}&background=random&color=fff`}
                        alt={story.user.username}
                        className="w-full h-full object-cover"
                      />
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex items-center mt-2 mb-0">
                  <span className="text-base truncate w-40 text-center">{story.user.username}</span>
                  {story.user.verified && (
                    <svg 
                      className="w-5 h-5 ml-1 text-blue-500" 
                      fill="currentColor" 
                      viewBox="0 0 20 20" 
                      xmlns="http://www.w3.org/2000/svg"
                      aria-label="Verified account"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
